import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Filter } from 'lucide-react';
import SearchBar from '../components/SearchBar/SearchBar';
import CategoryFilter from '../components/CategoryFilter/CategoryFilter';
import ResourceList from '../components/ResourceList/ResourceList';
import Pagination from '../components/Pagination/Pagination';
import SEO from '../components/SEO/SEO';
import { fetchResources, fetchTags } from '../services/resourcesApi';
import { SearchState } from '../models/SearchState';
import { Resource } from '../models/Resource';
import styles from './Resources.module.css';

// Error boundary component for graceful error handling
class ResourcesErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Resources page error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorBoundary}>
          <h2>Something went wrong</h2>
          <p>We encountered an error while loading the resources page.</p>
          <button
            onClick={() => window.location.reload()}
            className={styles.retryButton}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const Resources = () => {
  // URL state management
  const [searchParams, setSearchParams] = useSearchParams();
  

  // Component state
  const [searchState, setSearchState] = useState(() =>
    SearchState.fromURLSearchParams(searchParams)
  );
  const [resources, setResources] = useState([]);
  const [tags, setTags] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 12,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [loading, setLoading] = useState(true);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  // Viewport detection for mobile-specific UI tweaks
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });

  // Sync URL with search state
  const updateURL = useCallback((newSearchState, replace = false) => {
    const newSearch = newSearchState.toURLSearchParams().toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
    
    if (replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const newSearchState = SearchState.fromURLSearchParams(params);
      setSearchState(newSearchState);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Load tags for filtering on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        setTagsLoading(true);
        const tagsData = await fetchTags();
        setTags(tagsData);
      } catch (err) {
        console.error('Failed to load tags:', err);
        // Don't set error state for tags failure, just log it
      } finally {
        setTagsLoading(false);
      }
    };

    loadTags();
  }, []);

  // Load resources when search state changes
  useEffect(() => {
    const loadResources = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchResources(searchState);

        // Convert resources to Resource instances
        const resourceInstances = response.resources.map(resourceData =>
          new Resource(resourceData)
        );

        setResources(resourceInstances);
        setPagination(response.pagination);
      } catch (err) {
        console.error('Failed to load resources:', err);
        setError(err);
        setResources([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          pageSize: 12,
          hasNextPage: false,
          hasPreviousPage: false
        });
      } finally {
        setLoading(false);
      }
    };

    loadResources();
  }, [searchState]);

  // Update search state from URL params when location changes
  useEffect(() => {
    const newSearchState = SearchState.fromURLSearchParams(searchParams);
    setSearchState(newSearchState);
  }, [searchParams]);

  // Scroll to top when search state changes (except for pagination)
  useEffect(() => {
    // Only scroll to top if it's not just a page change
    const currentParams = searchState.toURLSearchParams();
    const newParams = SearchState.fromURLSearchParams(searchParams).toURLSearchParams();
    
    // Compare all params except page
    currentParams.delete('page');
    newParams.delete('page');
    
    if (currentParams.toString() !== newParams.toString()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [searchState.searchTerm, searchState.selectedCategories, searchState.pageSize]);

  // Handle search input (typing): update in-memory state only
  const handleSearch = useCallback((searchTerm) => {
    const newSearchState = searchState.updateSearchTerm(searchTerm);
    setSearchState(newSearchState);
    // Do NOT update URL while typing to preserve focus and avoid scroll
  }, [searchState]);

  // Handle committed search (Enter/Clear): update state and URL
  const handleSearchCommit = useCallback((searchTerm) => {
    const newSearchState = searchState.updateSearchTerm(searchTerm);
    setSearchState(newSearchState);
    updateURL(newSearchState, true);
  }, [searchState, updateURL]);

  // Handle tag filter changes
  const handleTagChange = useCallback((selectedTags) => {
    const newSearchState = searchState.updateSelectedCategories(selectedTags);
    setSearchState(newSearchState);
    // Replace URL for filters to avoid cluttering history
    updateURL(newSearchState, true);
  }, [searchState, updateURL]);

  // Handle page changes
  const handlePageChange = useCallback((page) => {
    // Ensure page is within valid range
    const newPage = Math.max(1, Math.min(page, pagination.totalPages));
    
    // Only update if page actually changed
    if (newPage !== searchState.currentPage) {
      const newSearchState = searchState.updateCurrentPage(newPage);
      setSearchState(newSearchState);
      updateURL(newSearchState, false);
      
      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [searchState, pagination.totalPages, updateURL]);

  // Handle page size changes
  const handlePageSizeChange = useCallback((pageSize) => {
    const newSearchState = searchState.updatePageSize(pageSize);
    setSearchState(newSearchState);
    // Replace URL for page size changes
    updateURL(newSearchState, true);
  }, [searchState, updateURL]);

  // Handle retry for error states
  const handleRetry = useCallback(() => {
    setError(null);
    // Trigger a reload by updating the search state
    setSearchState(searchState.clone());
  }, [searchState]);

  // Mobile sidebar handlers
  const openMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen(false);
    document.body.style.overflow = '';
  }, []);

  // Close mobile sidebar on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isMobileSidebarOpen) {
        closeMobileSidebar();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = ''; // Cleanup on unmount
    };
  }, [isMobileSidebarOpen, closeMobileSidebar]);

  // Listen for viewport size changes to update isMobile
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handleChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handleChange);
    } else {
      // Safari fallback
      mq.addListener(handleChange);
    }
    setIsMobile(mq.matches);
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', handleChange);
      } else {
        mq.removeListener(handleChange);
      }
    };
  }, []);

  // Memoized tag data for performance
  const tagData = useMemo(() => {
    return tags.map(tag => ({
      ...tag,
      hasChildren: () => tag.hasChildren(),
    }));
  }, [tags]);

  // Generate SEO meta tags based on current search state
  const seoData = useMemo(() => {
    let title = 'Resources';
    let description = 'Find installation guides, maintenance tips, product information, and troubleshooting help for ABC Railing products.';
    let keywords = 'aluminum railings, installation guides, maintenance tips, product guides, troubleshooting, ABC Railing resources';
    
    // Add search term to title and description
    if (searchState.searchTerm) {
      title = `${searchState.searchTerm} - Resources`;
      description = `Search results for "${searchState.searchTerm}" in ABC Railing resources. ${description}`;
      keywords = `${searchState.searchTerm}, ${keywords}`;
    }
    
    // Add tag information
    if (searchState.selectedCategories.length > 0) {
      const tagNames = searchState.selectedCategories
        .map(slug => tags.find(tag => tag.slug === slug)?.name)
        .filter(Boolean)
        .join(', ');
      
      if (tagNames) {
        title = searchState.searchTerm 
          ? `${searchState.searchTerm} in ${tagNames} - Resources`
          : `${tagNames} - Resources`;
        description = `Browse ${tagNames.toLowerCase()} resources. ${description}`;
        keywords = `${tagNames.toLowerCase()}, ${keywords}`;
      }
    }
    
    // Add pagination info for pages beyond first
    if (searchState.currentPage > 1) {
      title = `${title} - Page ${searchState.currentPage}`;
    }
    
    // Generate canonical URL
    const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}/resources` : '/resources';
    const searchParams = searchState.toURLSearchParams();
    const canonical = searchParams.toString() ? `${baseUrl}?${searchParams.toString()}` : baseUrl;
    
    // Structured data for search results
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": title,
      "description": description,
      "url": canonical,
      "mainEntity": {
        "@type": "ItemList",
        "numberOfItems": pagination.totalItems,
        "itemListElement": resources.map((resource, index) => ({
          "@type": "ListItem",
          "position": ((pagination.currentPage - 1) * pagination.pageSize) + index + 1,
          "item": {
            "@type": "Article",
            "name": resource.title,
            "description": resource.excerpt,
            "url": `${window.location.origin}${resource.getUrl()}`,
            "datePublished": resource.createdAt.toISOString(),
            "dateModified": resource.updatedAt.toISOString(),
            "author": {
              "@type": "Organization",
              "name": "ABC Railing"
            },
            "publisher": {
              "@type": "Organization",
              "name": "ABC Railing"
            }
          }
        }))
      }
    };
    
    return {
      title,
      description,
      keywords,
      canonical,
      structuredData: !loading && resources.length > 0 ? structuredData : null
    };
  }, [searchState, tags, pagination, resources, loading]);

  return (
    <ResourcesErrorBoundary>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        structuredData={seoData.structuredData}
      />
      <div className={styles.container}>


        <div className={styles.header}>
          <h1>Resources</h1>
          <p>Find installation guides, maintenance tips, product information, and troubleshooting help</p>
        </div>

        <div className={styles.contentLayout}>
          {/* Desktop Sidebar with tag filters */}
          <aside className={styles.sidebar}>
            <CategoryFilter
              categories={tagData}
              selectedCategories={searchState.selectedCategories}
              onCategoryChange={handleTagChange}
              showResourceCount={true}
              collapsible={true}
              className={tagsLoading ? styles.loading : ''}
            />
          </aside>

          {/* Main content area */}
          <main id="main-content" className={styles.mainContent}>
            {/* Search row with mobile filter icon button */}
            <div className={styles.searchRow}>
              <div className={styles.searchSection}>
                <SearchBar
                  onSearch={handleSearch}
                  onCommit={handleSearchCommit}
                  searchTerm={searchState.searchTerm}
                  placeholder={isMobile ? 'Search' : 'Search resources, guides, and documentation...'}
                  debounceMs={300}
                  className={styles.searchBarInline}
                />
              </div>
              <button
                className={styles.mobileFilterIconBtn}
                onClick={openMobileSidebar}
                aria-label="Open tag filters"
              >
                <Filter size={22} />
                {searchState.selectedCategories.length > 0 && (
                  <span className={styles.mobileFilterBadge}>
                    {searchState.selectedCategories.length}
                  </span>
                )}
              </button>
            </div>

            {/* Results summary */}
            {!loading && !error && pagination.totalItems > 0 && (
              <div className={styles.resultsSummary}>
                <p>
                  Showing {((pagination.currentPage - 1) * pagination.pageSize) + 1}-{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems} resources
                  {searchState.searchTerm && ` for "${searchState.searchTerm}"`}
                  {searchState.selectedCategories.length > 0 && (
                    <span> with {searchState.selectedCategories.length} tag{searchState.selectedCategories.length !== 1 ? 's' : ''}</span>
                  )}
                </p>
              </div>
            )}

            {/* Resource list */}
            <div className={styles.resourcesSection}>
              <ResourceList
                resources={resources}
                loading={loading}
                searchTerm={searchState.searchTerm}
                selectedCategories={searchState.selectedCategories}
                onRetry={handleRetry}
                error={error}
              />
            </div>

            {/* Pagination */}
            {!loading && !error && pagination.totalPages > 1 && (
              <div className={styles.paginationSection}>
                <Pagination
                  currentPage={searchState.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  pageSize={searchState.pageSize}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={[6, 12, 24, 48]}
                />
              </div>
            )}
          </main>
        </div>

        {/* Mobile filter popup overlay (rendered via portal to avoid stacking context issues) */}
        {isMobileSidebarOpen && createPortal(
          (
            <>
              <div
                className={`${styles.popupOverlay} ${styles.visible}`}
                onClick={closeMobileSidebar}
                aria-hidden={true}
              />

              {/* Mobile filter popup */}
              <div
                className={`${styles.mobileFilterPopup} ${styles.visible}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="mobile-popup-title"
              >
                <CategoryFilter
                  categories={tagData}
                  selectedCategories={searchState.selectedCategories}
                  onCategoryChange={handleTagChange}
                  showResourceCount={true}
                  collapsible={true}
                  className={tagsLoading ? styles.loading : ''}
                  showCloseButton={true}
                  onClose={closeMobileSidebar}
                />
              </div>
            </>
          ),
          typeof document !== 'undefined' ? document.body : null
        )}

        {/* Loading overlay for initial load */}
        {loading && !resources.length && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
              <p>Loading resources...</p>
            </div>
          </div>
        )}
      </div>
    </ResourcesErrorBoundary>
  );
};

export default Resources;